import { Component, HostListener, ViewChild } from "@angular/core";
import { NavigationExtras, Router } from "@angular/router";
import { DspxCardSetBaseFormWithHeaderComponent, FormHeaderModel, JsonObject, Option } from "dspx-core";
import { APP_DATA_KEYS, QUESTION_CODES, SECTION_CODES } from "../../constants/app-data.constants";
import { SetPreviousModelInputParameters } from "../../interfaces/set-previous-model-input-parameters.interface";
import { DeactivationGuard } from "../../interfaces/deactivation-guard.interface";
import { AppDataService } from "../../services/app-data.service";
import { Subscription } from "rxjs";
import { ResponseParserService } from "../../services/response-parser.service";

@Component({ template: '' })
export abstract class BaseCardSetDetailWithHeaderComponent implements DeactivationGuard {

  @ViewChild(DspxCardSetBaseFormWithHeaderComponent)
  protected baseForm: DspxCardSetBaseFormWithHeaderComponent;
  protected ignoreCanDeactivateGuard = false;
  protected subscriptions: Subscription[] = [];
  protected formHeaderModel: FormHeaderModel = {
    header: '',
    headerTitleLevel: 1,
    badgeText: '',
    badgeThemeClass: '',
    subtitle: '',
    subtitleLevel: 3,
    questions: []
  };

  protected constructor(
    protected router: Router,
    protected appDataService: AppDataService,
    protected responseParserService: ResponseParserService
  ) { }

  // Unsubscribe from the all the subscriptions registered
  // with 'registerSubscription'. Add this method to the
  // ngOnDestroy method on the component
  unsubscribeSubscriptions(): void {
    this.subscriptions.forEach((item) => {
      if (item instanceof Subscription) {
        try {
          item.unsubscribe();
        }
        catch (e) {
          console.log('There was an issue trying to unsubscribe from an observable.', e);
        }
      }
    });
  }

  // Registering a subscription through this method will allow it to
  // be unsubscribed when the component is destroyed. Ensure that
  // unsubscribeSubscriptions() is called in the components ngOnDestroy
  registerSubscription(subscription): void {
    this.subscriptions.push(subscription);
  }

  // returning true will navigate without confirmation
  // returning false will show a confirm alert before navigating away
  canDeactivate(): boolean {
    return this.canDeactivateBaseForm(this.baseForm);
  }

  canDeactivateBaseForm(baseForm: DspxCardSetBaseFormWithHeaderComponent): boolean {
    if (this.ignoreCanDeactivateGuard) {
      return true;
    }

    const previousModel = this.appDataService.getAppData(APP_DATA_KEYS.previousModel);
    const currentModel = baseForm.model;

    if (this.objectsAreEqual(previousModel, currentModel)) {
      return true;
    }

    return !baseForm.form.dirty;
  }

  private objectsAreEqual(previousModel: any, currentModel: any): boolean {
    // If only one of the two objects is false-y, return false;
    if (!currentModel != !previousModel) {
      return false;
    }

    // If both objects are false-y, return true
    if (!currentModel && !previousModel) {
      return true;
    }

    // Otherwise, compare
    const currentModelKeys = Object.keys(currentModel);

    for (const key of currentModelKeys) {
      if (key !== '$state' && key !== 'isEditing') {
        const previousValue = previousModel[key];
        const currentValue = currentModel[key];
        const bothAreObjects = this.isObject(previousValue) && this.isObject(currentValue);
        //Using loose equality for previousValue and currentValue here so that values like "3" and 3 are treated as equal.
        if ((bothAreObjects && !this.objectsAreEqual(previousValue, currentValue)) || (!bothAreObjects && previousValue != currentValue)) {
          return false;
        }
      }
    }
    return true;
  }

  private isObject(object: any): boolean {
    return object !== null && typeof object === 'object';
  }

  protected navigate = (commands: any[], ignoreUnsavedChanges = false, extras: NavigationExtras = undefined) => {
    this.ignoreCanDeactivateGuard = ignoreUnsavedChanges;
    this.router.navigate(commands, extras);
  }

  protected reloadPage(ignoreUnsavedChanges = false) {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
    this.navigate([this.router.url], ignoreUnsavedChanges);
  }

  protected resetDirtyFlagAfterLoading() {
    // This is just a lame workaround to address the fact that empty forms containing repeating pages/sections,
    // if they are configured to expand the first empty element on load, then they will cause the dirty flag to be set even if the user has not touched the form yet.
    setTimeout(() => this.baseForm?.form?.markAsPristine(), 200);
  }

  // The purpose of this method is to try to store the current model (and mark the form as pristine) after loading the form
  // and after all initial model changes have happened.
  //
  // By default the method runs synchronously, for those scenarios when there are no asynchonous model updates happening afterwards
  // (for example, after saving a draft).
  //
  // However, for those scenarios when there are asynchronous model updates happening afterwards (for example, after the initial load),
  // this method can be configured to run asynchronously.
  // Since every form has a slightly different implementation, this method adopts a dumb "black box" strategy,
  // by simply retrying (with delays) until the model has stopped changing.
  // It retries up to maxAttempts times (default = 3), with waits of delayBetweenAttemptsMs ms (default: 100) between attempts.
  protected setPreviousModel(params?: SetPreviousModelInputParameters): void {
    this.setPreviousModelForBaseForm(this.baseForm, params);
  }
  protected setPreviousModelForBaseForm(baseForm: DspxCardSetBaseFormWithHeaderComponent, params?: SetPreviousModelInputParameters): void {
    if (!baseForm?.model) {
      return;
    }

    const getCurrentModelAsString = () => JSON.stringify(baseForm.model);

    const saveAsPreviousModel = (modelAsString: any) => {
      baseForm.form.markAsPristine()
      this.appDataService.setAppData(APP_DATA_KEYS.previousModel, JSON.parse(modelAsString));
    };

    const isAsync = params?.isAsync ?? false;
    if (!isAsync) {
      saveAsPreviousModel(getCurrentModelAsString());
      return;
    }

    const maxAttempts = params?.maxAttempts ?? 3;
    const delayBetweenAttemptsMs = params?.delayBetweenAttemptsMs ?? 100;

    let previousModelAsString = JSON.stringify(baseForm.model);
    let attemptIndex = 0;
    const waitUntilModelStopsChanging = () => {
      setTimeout(
        () => {
          const currentModelAsString = getCurrentModelAsString();
          if (++attemptIndex === maxAttempts || currentModelAsString === previousModelAsString) {
            saveAsPreviousModel(currentModelAsString);
            return;
          }
          previousModelAsString = currentModelAsString;
          waitUntilModelStopsChanging();
        },
        delayBetweenAttemptsMs);
    }
    waitUntilModelStopsChanging();
  }

  // @HostListener allows us to also guard against browser refresh, close, etc.
  @HostListener('window:beforeunload', ['$event'])
  protected unloadNotification = (event: any) => {
    if (!this.canDeactivate()) {
      // When showing the "confirm" popup on navigating without using Angular routing (i.e. type another URL / close the browser / etc.),
      // some browsers (e.g. Internet Explorer, the old non-Chromium version of Edge) will use the message below,
      // whereas other browsers (e.g. Chrome, Firefox) will override it with their own message
      event.returnValue = "WARNING: You have unsaved changes. Press Cancel to go back and save these changes, or OK to discard these changes.";
    }
  }

  questionsUpdated(previousSection: JsonObject, currentSection: JsonObject, questionCodes: string[]): boolean {

    return (previousSection !== undefined && previousSection !== null &&
      currentSection !== undefined && currentSection !== null &&
      questionCodes.some(
        questionCode =>
        (
          (previousSection[questionCode] && !currentSection[questionCode])
          ||
          (!previousSection[questionCode] && currentSection[questionCode])
          ||
          (previousSection[questionCode] && currentSection[questionCode] && previousSection[questionCode] !== currentSection[questionCode])
        )
      ))
      ||
      ((previousSection === undefined || previousSection === null) && currentSection !== undefined && currentSection !== null)
      ||
      (previousSection !== undefined && previousSection !== null && (currentSection === undefined || currentSection === null));
  }

  getSectionModel(formModel: JsonObject, section: { navTabNumber: number, pageNumber: number, sectionCode: string }): JsonObject {
    return formModel?.['form']?.['tabs']?.[`tab${section.navTabNumber}`]?.[`page${section.pageNumber}`]?.[section.sectionCode];
  }

  public handleDialogButtonClick($event: any, popupCode: string): boolean {
    let segments = [
      $event?.field?.key,
      $event?.event?.type,
      $event?.event?.colName,
      $event?.event?.data?.buttonType,
      $event?.field?.field?.key,
      $event?.field?.type,
      popupCode,
      'Clicked'
    ];

    segments = segments.filter((x) => !!x);

    const handlerName = 'this.dialogButton_' + segments.join('_').replace('-', '');

    let handler = eval(handlerName);

    if (handler) {
      handler = handler.bind(this);
      handler($event);
      return true;
    } else {
      console.warn(`Could not find dialog button click handler. Expected ${handlerName}. Source event: `, $event);
      return false;
    }
  }

  
}
